import { useState, useEffect, useCallback, useMemo, useRef } from "react";

/**
 * useLocalStorage is a hook for managing data with the localStorage API.
 * It provides cross-tab synchronization and safe interaction with localStorage.
 *
 * @param {string} localStorageKey - The key under which the value is stored in localStorage.
 * @param {T} initialValue - The initial value of the data to be stored.
 *
 * Note: The object T should be able to be stringified, as it will be stored in localStorage as a string.
 *
 * @return An array with three elements:
 *     value: Current value
 *     setValue: Function to update the value and sync across tabs
 *     clearValue: Function to remove value from the local storage.
 *                This function will also reset the value to initial value
 *
 * @template T - The type of the data to be stored in localStorage. It should be a type that can be stringified.
 *
 * @throws Will throw an error if the stringifying the value or accessing local storage fails.
 *
 * @example
 * const [theme, setTheme, clearTheme] = useLocalStorage('theme', 'light');
 * // Use theme value
 * // Call setTheme to update
 * // Call clearTheme to reset to 'light'
 */
function useLocalStorage<T>(
  localStorageKey: string,
  initialValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>, () => void] {
  // Initialize state with value from localStorage or initial value
  // This initialization is only run once when the component mounts
  const [value, setValue] = useState<T>(() => {
    // Return initial value if running on server-side
    if (typeof window === "undefined") return initialValue;

    try {
      const stored = localStorage.getItem(localStorageKey);
      // Parse stored value if it exists, otherwise use initial value
      return stored ? (JSON.parse(stored) as T) : initialValue;
    } catch (error) {
      console.warn("Error reading from local storage", error);
      return initialValue;
    }
  });

  // Helper object to safely interact with localStorage
  // Handles all error cases and provides consistent interface
  const safeLocalStorage = useMemo(
    () => ({
      set: (value: T) => {
        try {
          const stringified = JSON.stringify(value);
          localStorage.setItem(localStorageKey, stringified);
          return stringified;
        } catch (error) {
          console.warn("Error writing to local storage", error);
          return null;
        }
      },
      remove: () => {
        try {
          localStorage.removeItem(localStorageKey);
        } catch (error) {
          console.warn("Error clearing local storage", error);
        }
      },
    }),
    [localStorageKey],
  );

  // Function to clear both localStorage and state
  const clearValue = () => {
    safeLocalStorage.remove();
    setValue(initialValue);
  };

  // Whether the current value came from this instance's own setter, and so is
  // the one the other instances in this tab still have to be told about. A
  // value that arrived FROM one of them is never re-announced.
  const isOwnWriteRef = useRef(false);

  // Sync to localStorage whenever value changes, and — for a value this
  // instance set itself — notify the other instances in this tab.
  //
  // Both are side effects, so they run here and never inside the state
  // updater. React invokes an updater during the render phase (and twice in
  // StrictMode), so dispatching from there calls setState on every other
  // subscriber to this key while a component is rendering: React's "Cannot
  // update a component while rendering a different component".
  useEffect(() => {
    const stringified = safeLocalStorage.set(value);
    if (!isOwnWriteRef.current) return;
    isOwnWriteRef.current = false;
    if (stringified === null) return;
    window.dispatchEvent(
      new CustomEvent("localStorageChange", {
        detail: { key: localStorageKey, newValue: stringified },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStorageKey, value]);

  // Handle cross-tab synchronization
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Handler for native localStorage events (triggered by other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === localStorageKey) {
        try {
          isOwnWriteRef.current = false;
          setValue(e.newValue ? (JSON.parse(e.newValue) as T) : initialValue);
        } catch (error) {
          console.warn("Error parsing storage change", error);
        }
      }
    };

    // Handler for custom events (triggered within same tab)
    const handleCustomEvent = (
      e: CustomEvent<{ key: string; newValue: string }>,
    ) => {
      if (e.detail.key === localStorageKey) {
        try {
          isOwnWriteRef.current = false;
          setValue(
            e.detail.newValue
              ? (JSON.parse(e.detail.newValue) as T)
              : initialValue,
          );
        } catch (error) {
          console.warn("Error parsing custom event", error);
        }
      }
    };

    // Listen for both storage events and custom events
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(
      "localStorageChange",
      handleCustomEvent as EventListener,
    );

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "localStorageChange",
        handleCustomEvent as EventListener,
      );
    };
  }, [localStorageKey, initialValue]);

  // Enhanced setValue that also persists and notifies the other instances in
  // this tab — from the effect above, so this stays a plain state update and
  // the updater React runs stays pure. Updater functions pass straight through.
  const setValueAndNotify: React.Dispatch<React.SetStateAction<T>> =
    useCallback((newValue) => {
      isOwnWriteRef.current = true;
      setValue(newValue);
    }, []);

  return [value, setValueAndNotify, clearValue] as const;
}

export default useLocalStorage;
